import { MonthlyClosureService } from "../services";

const initialPeriod = () => {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };
};

const normalizeClosureDocuments = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) return source;

  const documents = source.documents !== undefined ? source.documents : source;
  if (Array.isArray(documents)) return documents;
  if (!documents || typeof documents !== "object") return [];

  const result = [];
  if (documents.buildingSummary) {
    result.push({
      ...documents.buildingSummary,
      documentRole:
        documents.buildingSummary.documentRole ||
        documents.buildingSummary.metadata?.documentRole ||
        "building_summary",
    });
  }

  const apartmentLiquidations = documents.apartmentLiquidations || [];
  if (Array.isArray(apartmentLiquidations)) {
    apartmentLiquidations.forEach((document) => {
      result.push({
        ...document,
        documentRole:
          document.documentRole ||
          document.metadata?.documentRole ||
          "apartment_liquidation",
      });
    });
  }

  return result;
};

const buildClosurePreview = (closure) => {
  if (!closure) return null;

  const metadata = closure.metadata || {};
  const totals = closure.totals || {
    totalIncome: closure.totalIncome,
    totalExpenses: closure.totalExpenses,
    totalFines: closure.totalFines,
    totalCommonExpenses: closure.totalCommonExpenses,
    totalReserveFund: closure.totalReserveFund,
    totalToPay: closure.totalToPay || metadata.totalToPay,
    totalApartments: closure.totalApartments || metadata.totalApartments,
    totalApportionment: closure.totalApportionment || metadata.totalApportionment,
  };
  const details = closure.apartments || closure.details || closure.monthlyClosureDetails || [];
  const apartments = details.map((detail) => ({
    ...(detail.metadata || {}),
    ...detail,
    apartmentNumber:
      detail.apartmentNumber ||
      detail.metadata?.apartmentNumber ||
      detail.apartment?.number,
    towerName: detail.towerName || detail.metadata?.towerName || detail.tower?.name,
    ownerName: detail.ownerName || detail.metadata?.owner,
    residentName: detail.residentName || detail.metadata?.resident,
    concepts: detail.concepts || detail.metadata?.concepts || [],
  }));

  return {
    ok: true,
    closureId: closure.id || closure.closureId,
    buildingId: closure.buildingId,
    status: closure.status,
    closedAt: closure.closedAt,
    period: closure.period || {
      month: closure.month,
      year: closure.year,
      periodStart: closure.periodStart,
      periodEnd: closure.periodEnd,
    },
    month: closure.month,
    year: closure.year,
    totals,
    accountSummary: closure.accountSummary || metadata.accountSummary || [],
    incomes: closure.incomes || metadata.incomes || [],
    expenses: closure.expenses || metadata.expenses || [],
    fines: closure.fines || metadata.fines || [],
    apartments,
    warnings: closure.warnings || metadata.warnings || [],
    configurationSnapshot: closure.configurationSnapshot || {
      closingTypeSnapshot: closure.closingTypeSnapshot,
      reserveFundTypeSnapshot: closure.reserveFundTypeSnapshot,
      reserveFundValueSnapshot: closure.reserveFundValueSnapshot,
      reserveFundPercentageSnapshot: closure.reserveFundPercentageSnapshot,
    },
    documents: normalizeClosureDocuments(closure.documents || []),
  };
};

export const monthlyClosure = {
  state: () => ({
    closures: [],
    currentClosure: null,
    closureId: null,
    status: null,
    closedAt: null,
    preview: null,
    documents: [],
    sendResult: null,
    warnings: [],
    loading: false,
    error: null,
    selectedPeriod: initialPeriod(),
  }),
  mutations: {
    setMonthlyClosures: (state, value) => (state.closures = value || []),
    setCurrentMonthlyClosure: (state, value) => {
      state.currentClosure = value || null;
      state.closureId = value?.closureId || value?.id || null;
      state.status = value?.status || null;
      state.closedAt = value?.closedAt || null;
    },
    setMonthlyClosureGeneratedState: (state, value) => {
      state.closureId = value?.closureId || value?.id || null;
      state.status = value?.status || null;
      state.closedAt = value?.closedAt || null;
    },
    setMonthlyClosurePreview: (state, value) => {
      state.preview = value || null;
      state.warnings = value?.warnings || [];
      if (!value) {
        state.closureId = null;
        state.status = null;
        state.closedAt = null;
        state.currentClosure = null;
      }
    },
    setMonthlyClosureDocuments: (state, value) =>
      (state.documents = normalizeClosureDocuments(value)),
    setCurrentMonthlyClosureDocuments: (state, value) => {
      const documents = normalizeClosureDocuments(value);
      if (state.currentClosure) {
        state.currentClosure = { ...state.currentClosure, documents };
      }
      state.documents = documents;
    },
    setMonthlyClosureSendResult: (state, value) =>
      (state.sendResult = value || null),
    setMonthlyClosureWarnings: (state, value) => (state.warnings = value || []),
    setMonthlyClosureLoading: (state, value) => (state.loading = Boolean(value)),
    setMonthlyClosureError: (state, value) => (state.error = value || null),
    setMonthlyClosureSelectedPeriod: (state, value) =>
      (state.selectedPeriod = { ...state.selectedPeriod, ...(value || {}) }),
    clearMonthlyClosurePreview: (state) => {
      state.preview = null;
      state.documents = [];
      state.sendResult = null;
      state.warnings = [];
      state.error = null;
      state.closureId = null;
      state.status = null;
      state.closedAt = null;
      state.currentClosure = null;
    },
  },
  actions: {
    MONTHLY_CLOSURE_FETCH_ALL: async ({ commit }, params = {}) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.list(params);
        if (response.ok) {
          commit("setMonthlyClosures", response.data || []);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_FETCH_CLOSURES: async ({ dispatch }, params = {}) =>
      dispatch("MONTHLY_CLOSURE_FETCH_ALL", params),
    MONTHLY_CLOSURE_FETCH_ONE: async ({ commit }, id) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.get(id);
        if (response.ok) {
          const closure = response.data || response.closure || response;
          const preview = buildClosurePreview(closure);
          commit("setCurrentMonthlyClosure", closure || null);
          commit("setMonthlyClosurePreview", preview);
          commit("setMonthlyClosureGeneratedState", {
            closureId: preview?.closureId,
            status: preview?.status,
            closedAt: preview?.closedAt,
          });
          commit("setMonthlyClosureDocuments", closure || []);
          commit("setMonthlyClosureWarnings", preview?.warnings || []);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_PREVIEW: async ({ commit }, payload) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.preview(payload);
        if (response.ok) {
          commit("setCurrentMonthlyClosure", null);
          commit("setMonthlyClosureGeneratedState", null);
          commit("setMonthlyClosurePreview", response);
        } else {
          commit("setMonthlyClosurePreview", null);
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_GENERATE: async ({ commit, state }, payload) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.generate(payload);
        if (response.ok) {
          const closureId = response.closureId || response.data?.id || response.id;
          const status = response.status || response.data?.status || "draft";
          const preview = {
            ...(state.preview || {}),
            ...response,
            buildingId: response.buildingId || payload?.buildingId,
            status,
            warnings: response.warnings || [],
            configurationSnapshot:
              response.configurationSnapshot ||
              state.preview?.configurationSnapshot ||
              {},
          };

          commit("setCurrentMonthlyClosure", response.data || response);
          commit("setMonthlyClosurePreview", preview);
          commit("setMonthlyClosureGeneratedState", { closureId, status });
          commit("setMonthlyClosureWarnings", response.warnings || []);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_CLOSE: async ({ commit, state }, id) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.close(id);
        if (response.ok) {
          const closure = response.data || response;
          const closureId = response.closureId || closure?.id || id;
          const status = response.status || closure?.status || "closed";
          const closedAt = response.closedAt || closure?.closedAt || null;
          const preview = {
            ...(state.preview || {}),
            status,
            closedAt,
            warnings: response.warnings || state.warnings || [],
          };

          commit("setCurrentMonthlyClosure", closure);
          commit("setMonthlyClosurePreview", preview);
          commit("setMonthlyClosureGeneratedState", {
            closureId,
            status,
            closedAt,
          });
          commit("setMonthlyClosureWarnings", response.warnings || state.warnings || []);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_GENERATE_DOCUMENTS: async ({ commit }, id) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.generateDocuments(id);
        if (response.ok) {
          commit("setCurrentMonthlyClosureDocuments", response);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_SEND_DOCUMENTS: async ({ commit }, id) => {
      commit("setMonthlyClosureLoading", true);
      commit("setMonthlyClosureError", null);
      try {
        const response = await MonthlyClosureService.sendDocuments(id);
        if (response.ok) {
          commit("setMonthlyClosureSendResult", response);
          commit("setMonthlyClosureWarnings", response.warnings || []);
        } else {
          commit("setMonthlyClosureError", response.message);
        }
        return response;
      } finally {
        commit("setMonthlyClosureLoading", false);
      }
    },
    MONTHLY_CLOSURE_CLEAR_PREVIEW: ({ commit }) => {
      commit("clearMonthlyClosurePreview");
    },
    MONTHLY_CLOSURE_SET_SELECTED_PERIOD: ({ commit }, payload) => {
      commit("setMonthlyClosureSelectedPeriod", payload);
    },
  },
  getters: {
    monthlyClosureTotals: (state) => state.preview?.totals || {},
    monthlyClosureWarnings: (state) => state.warnings || [],
    monthlyClosureIncomes: (state) => state.preview?.incomes || [],
    monthlyClosureExpenses: (state) => state.preview?.expenses || [],
    monthlyClosureFines: (state) => state.preview?.fines || [],
    monthlyClosureAccountSummary: (state) =>
      state.preview?.accountSummary || [],
    monthlyClosureApartments: (state) => state.preview?.apartments || [],
  },
};
